import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { MapPin } from 'lucide-react';

// Simple address autocomplete using a debounced search
// In production, integrate with Google Places API for better results
export const AddressAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Enter address...",
  className = "",
  ...props 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Common Canadian city suggestions based on input
  const getCanadianAddressSuggestions = (query) => {
    if (!query || query.length < 3) return [];
    
    const lowerQuery = query.toLowerCase();
    
    // Canadian cities and common street patterns
    const canadianLocations = [
      { city: "Toronto", province: "ON", postalPrefix: "M" },
      { city: "Vancouver", province: "BC", postalPrefix: "V" },
      { city: "Montreal", province: "QC", postalPrefix: "H" },
      { city: "Calgary", province: "AB", postalPrefix: "T" },
      { city: "Edmonton", province: "AB", postalPrefix: "T" },
      { city: "Ottawa", province: "ON", postalPrefix: "K" },
      { city: "Mississauga", province: "ON", postalPrefix: "L" },
      { city: "Winnipeg", province: "MB", postalPrefix: "R" },
      { city: "Quebec City", province: "QC", postalPrefix: "G" },
      { city: "Hamilton", province: "ON", postalPrefix: "L" },
      { city: "Kitchener", province: "ON", postalPrefix: "N" },
      { city: "London", province: "ON", postalPrefix: "N" },
      { city: "Victoria", province: "BC", postalPrefix: "V" },
      { city: "Halifax", province: "NS", postalPrefix: "B" },
      { city: "Windsor", province: "ON", postalPrefix: "N" },
      { city: "Saskatoon", province: "SK", postalPrefix: "S" },
      { city: "Regina", province: "SK", postalPrefix: "S" },
      { city: "St. John's", province: "NL", postalPrefix: "A" },
      { city: "Brampton", province: "ON", postalPrefix: "L" },
      { city: "Surrey", province: "BC", postalPrefix: "V" },
    ];

    const streetTypes = ["Street", "Avenue", "Road", "Drive", "Boulevard", "Way", "Court", "Place", "Lane", "Crescent"];
    
    const suggestions = [];
    
    // Check if query matches a city
    canadianLocations.forEach(loc => {
      if (loc.city.toLowerCase().includes(lowerQuery)) {
        suggestions.push(`${loc.city}, ${loc.province}, Canada`);
      }
    });
    
    // If query looks like a street number + name, generate suggestions
    const streetMatch = query.match(/^(\d+)\s+(.+)/);
    if (streetMatch) {
      const [, number, streetName] = streetMatch;
      const capitalizedStreet = streetName.charAt(0).toUpperCase() + streetName.slice(1);
      
      streetTypes.slice(0, 3).forEach(type => {
        canadianLocations.slice(0, 5).forEach(loc => {
          suggestions.push(`${number} ${capitalizedStreet} ${type}, ${loc.city}, ${loc.province}`);
        });
      });
    }
    
    return suggestions.slice(0, 6);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Debounce the suggestions
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    debounceRef.current = setTimeout(() => {
      if (newValue.length >= 3) {
        setLoading(true);
        const newSuggestions = getCanadianAddressSuggestions(newValue);
        setSuggestions(newSuggestions);
        setShowSuggestions(newSuggestions.length > 0);
        setLoading(false);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={`pl-10 ${className}`}
          autoComplete="off"
          {...props}
        />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 transition-colors"
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="truncate">{suggestion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
